import {
	normalizePurchaseLines,
	type PurchaseLine,
} from "./hub-purchase-lines.js";
import type { HubSalesOrderDoc } from "./hub-work-sales-order.js";
import { salesOrderIdOf } from "./hub-work-sales-order.js";

export type HubQuotationDoc = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	status: string;
	extra?: string;
	note: string;
	lines?: PurchaseLine[];
	salesOrderId?: number | string;
};

export type HubSalesOrderWithQuotation = HubSalesOrderDoc & {
	quotationId?: number | string;
};

export function quotationIdOf(value: number | string | undefined | null): number | null {
	if (value == null || value === "") return null;
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

export function findQuotationById(quotations: HubQuotationDoc[], quotationId: number): HubQuotationDoc | undefined {
	return quotations.find((row) => row.id === quotationId);
}

export function quotationDetailPath(quote: HubQuotationDoc): string {
	const key = encodeURIComponent(quote.ref_no?.trim() || String(quote.id ?? ""));
	return `/hub/quotations/${key}`;
}

export function quotationAcceptsSalesOrderConversion(status: string): boolean {
	return status.trim().toLowerCase() === "accepted";
}

export function linkedSalesOrderForQuotation(
	quote: HubQuotationDoc,
	salesOrders: HubSalesOrderWithQuotation[],
): HubSalesOrderWithQuotation | undefined {
	const fromQuote = salesOrderIdOf(quote.salesOrderId);
	if (fromQuote != null) {
		const byId = salesOrders.find((row) => row.id === fromQuote);
		if (byId) return byId;
	}
	if (quote.id == null) return undefined;
	return salesOrders.find((row) => quotationIdOf(row.quotationId) === quote.id);
}

export function linkedQuotationForSalesOrder(
	order: HubSalesOrderWithQuotation,
	quotations: HubQuotationDoc[],
): HubQuotationDoc | undefined {
	const qid = quotationIdOf(order.quotationId);
	if (qid == null) return undefined;
	return findQuotationById(quotations, qid);
}

export function quotationDeletionBlockedReason(
	quote: HubQuotationDoc,
	salesOrders: HubSalesOrderWithQuotation[],
): string | null {
	if (quote.id == null) return null;
	const order = linkedSalesOrderForQuotation(quote, salesOrders);
	if (!order) return null;
	const ref = order.ref_no?.trim() || (order.id != null ? `#${order.id}` : "sales order");
	return `This quotation is linked to sales order ${ref}. Delete that sales order first, then try again.`;
}

export function quotationConversionBlockedReason(
	quote: HubQuotationDoc,
	salesOrders: HubSalesOrderWithQuotation[],
): string | null {
	if (quote.id == null) return "Save this quotation before creating a sales order.";
	if (!quotationAcceptsSalesOrderConversion(quote.status)) return "Set the quotation to Accepted before creating a sales order.";
	if (linkedSalesOrderForQuotation(quote, salesOrders)) return "This quotation already has a linked sales order.";
	const lines = normalizePurchaseLines(quote.lines ?? []);
	if (lines.length === 0) return "Add at least one line item to the quotation.";
	return null;
}

export function purchaseLineRecordsFromQuotation(lines: PurchaseLine[] | undefined) {
	return normalizePurchaseLines(lines ?? []).map((line) => ({
		category: line.category ?? "other",
		description: line.description,
		amount: line.amount,
		qty: line.qty,
		unitPrice: line.unitPrice,
	}));
}

export function buildSalesOrderRecordFromQuotation(
	quote: HubQuotationDoc,
	refNo: string,
	docAt: string,
): Record<string, unknown> {
	if (quote.id == null) throw new Error("Quotation id required");
	return {
		party: quote.party,
		doc_at: docAt,
		extra: quote.extra ?? "",
		ref_no: refNo,
		status: "draft",
		mark: "",
		note: quote.note ?? "",
		lines: purchaseLineRecordsFromQuotation(quote.lines),
		quotationId: quote.id,
	};
}

export function quotationRecordWithSalesOrderLink(
	quote: HubQuotationDoc,
	salesOrderId: number | null,
): Record<string, unknown> {
	const record: Record<string, unknown> = { ...quote };
	delete record.id;
	if (salesOrderId == null) delete record.salesOrderId;
	else record.salesOrderId = salesOrderId;
	return record;
}
